/**
 * Step four — `PackageBuilder`, then `retainers_start$`. The money step.
 *
 * `retainers_start` is on `actionFields.ts`'s `HAND_WRITTEN` list and its own entry
 * says why: `items[].included_widget_qty` is an array of objects inside an array of
 * objects, and the price has to be computed live as the blocks change. So the form is
 * written here, and the two rules it exists to keep are:
 *
 *   1. **Nothing on this screen computes a price.** `PackageBuilder` calls
 *      `catalog_compute_price` with the client's own tier and currency and hands the
 *      answer back; the confirm button and the `amount` field both read `total` off
 *      that same object. `retainers_start`'s `amount` is a **stated** figure the
 *      service re-checks against the retainer it is about to write — so a builder
 *      that added block prices up in the browser would be refused the first time
 *      somebody crossed a bundle band, which is the point of stating it.
 *   2. **A `$` action confirms with the amount** (`.docs/20` §Design decisions). The
 *      button says what starting the retainer costs every month, and an outside click
 *      does not dismiss it (`ConfirmDialog`'s own `modalOnly`).
 *
 * The presets are `catalog_list_packages`. Choosing one fills the builder in and then
 * gets out of the way: `retainers_start` takes "a package code **or** items, not
 * both", and a preset somebody has since edited is no longer that preset — so this
 * always sends `items`, and the preset's name is only a suggestion for the name field.
 */

import {
  RetainerSchema,
  StartRetainerSchema,
  type ComputedPrice,
  type PackageItemInput,
} from '@fareast/shared'
import { useState } from 'react'
import { z } from 'zod'

import { messageOf } from '../../../api/problem.js'
import { reviewOf, useStudioAction, useStudioRead } from '../../../api/studio.js'
import { Button, ButtonRow } from '../../../components/portal/Button.js'
import { Empty } from '../../../components/portal/Disclosure.js'
import { Input, useZodForm } from '../../../components/portal/Form.js'
import { Stack } from '../../../components/portal/Layout.js'
import { ConfirmDialog } from '../../../components/portal/Overlay.js'
import { Notice } from '../../../components/portal/Status.js'
import { Muted, Prose, Small } from '../../../components/portal/Text.js'
import { NeedsReviewNotice } from '../../../components/studio/NeedsReviewNotice.js'
import { PackageBuilder } from '../../../components/studio/PackageBuilder.js'
import { useFormat } from '../../../format/useFormat.js'
import { useAccount } from './account.js'

/** The retainer's own name, and nothing else. The blocks are the builder's. */
const NameSchema = z.object({ name: StartRetainerSchema.shape.name })

type NameValues = z.infer<typeof NameSchema>

export type PackageStepProps = {
  readonly client: string
  /** Run once the retainer exists. The wizard moves to the last step. */
  readonly onStarted: () => void
}

export function PackageStep({ client, onStarted }: PackageStepProps) {
  const format = useFormat()

  const { account } = useAccount(client)
  const catalog = useStudioRead('catalog_list_services', {})
  const presets = useStudioRead('catalog_list_packages', { templates_only: true })

  const [items, setItems] = useState<readonly PackageItemInput[]>([])
  const [price, setPrice] = useState<ComputedPrice | null>(null)
  const [confirming, setConfirming] = useState(false)

  const form = useZodForm<NameValues, NameValues>(NameSchema, { defaultValues: { name: '' } })
  const start = useStudioAction('retainers_start')
  const review = reviewOf(start.data)

  if (account === null || catalog.data === undefined || presets.data === undefined) {
    return <Empty>Reading the account and the rate card…</Empty>
  }

  const row = account.client

  const send = async (name: string) => {
    setConfirming(false)
    if (price === null) return
    try {
      const output = await start.mutateAsync({
        client,
        name,
        items: [...items],
        /*
         * **Onboarding creates none**, and says so rather than leaving it to the
         * schema's default. A custom line is a fixed amount agreed with one client
         * outside the card (`PRICING.md` §Custom lines); it is added afterwards with
         * `retainers_change_package`, by somebody who has the agreement in front of
         * them. `PackageBuilder` is handed no `customLines` for the same reason, so
         * the figure on the confirm button is the blocks and nothing else.
         */
        custom_lines: [],
        amount: price.total,
      })
      /*
       * A 202 is a success with a task reference in it rather than a retainer
       * (`api/studio.ts` `reviewOf`), so the wizard stays where it is and the notice
       * says who has to look at it. Parsing the other branch is what makes "the
       * retainer exists" a fact rather than an assumption the next step is built on.
       */
      if (reviewOf(output) === null) {
        RetainerSchema.parse(output)
        onStarted()
      }
    } catch {
      /* Rendered from `start.error` below. `mutateAsync` rejects; nothing else here does. */
    }
  }

  /*
   * The name is validated before the dialog opens, not after it: a confirm dialog
   * that names an amount and then fails on a field behind it is a dialog that lied.
   */
  const open = form.handleSubmit(() => {
    setConfirming(true)
  })

  const monthly = price === null ? null : format.money(price.total)
  const ready = items.length > 0 && monthly !== null

  return (
    <Stack gap="loose">
      {/*
        The sentence used to name the tool — `catalog_compute_price` — which is the
        right way to say "the same function" to somebody who can call it and no way at
        all to say it to somebody who cannot. The fact a studio member needs is the
        second half of it, and it is now the whole of it.
      */}
      <Prose>
        Priced at market tier <b>{format.number(row.market_tier)}</b> in{' '}
        <b>{row.billing_currency}</b>, which is what the account was opened with. Every figure below
        is the rate card&rsquo;s, worked out the same way the invoice will work it out, so the
        number on the button is the number on the bill.
      </Prose>

      <form
        noValidate
        onSubmit={(event) => {
          void open(event)
        }}
      >
        <Stack>
          <Input
            control={form.control}
            hint="What the client calls their package — “Growth+”. It is on every invoice."
            label="Package name"
            name="name"
          />

          <Stack gap="tight">
            <Small>
              <Muted>Start from a preset, then change it:</Muted>
            </Small>
            <ButtonRow>
              {presets.data.packages.map((preset) => (
                <Button
                  key={preset.code}
                  onClick={() => {
                    setItems(preset.items.map((one) => ({ ...one })))
                    form.setValue('name', preset.name, { shouldValidate: true })
                  }}
                  size="sm"
                >
                  {preset.name}
                </Button>
              ))}
              <Button
                onClick={() => {
                  setItems([])
                }}
                size="sm"
                variant="ghost"
              >
                Empty it
              </Button>
            </ButtonRow>
          </Stack>

          <PackageBuilder
            currency={row.billing_currency}
            onChange={setItems}
            onPriced={setPrice}
            services={catalog.data.services}
            tier={row.market_tier}
            value={items}
          />

          {review === null ? null : <NeedsReviewNotice review={review} />}
          {start.isError ? (
            <Notice need role="alert">
              {messageOf(start.error)}
            </Notice>
          ) : null}

          <ButtonRow>
            <Button disabled={!ready || start.isPending} type="submit" variant="ochre">
              {start.isPending ? 'Starting…' : 'Start the retainer'}
            </Button>
          </ButtonRow>
        </Stack>
      </form>

      {monthly === null ? null : (
        <ConfirmDialog
          busy={start.isPending}
          confirmLabel={`Start the retainer at ${monthly} a month`}
          onConfirm={() => {
            void send(form.getValues().name)
          }}
          onOpenChange={(next) => {
            if (!next) setConfirming(false)
          }}
          open={confirming}
          title="Start a retainer"
        >
          <Prose>
            This bills {row.name}. The signup invoice carries the rest of this month, prorated by
            calendar days, plus the first full month; monthly billing begins after that. The blocks,
            their allowances and their overage prices are snapshotted from today&rsquo;s rate card,
            and a later price change does not move them.
          </Prose>
        </ConfirmDialog>
      )}
    </Stack>
  )
}
